"use client";

import { FC } from "react";
import {
  StellarWalletsKit,
  WalletNetwork,
  allowAllModules,
  XBULL_ID,
} from "@creit.tech/stellar-wallets-kit";
import { useWallet } from "@/contexts/WalletContext";
import {
  AccountResponse,
  BalanceLine,
  BalanceLineNative,
  BalanceLineAsset,
  BalanceLineLiquidityPool,
} from "@/utils/types";
import { Horizon } from "@stellar/stellar-sdk/minimal";
import { Networks } from "@stellar/stellar-sdk";

export const ConnectWallet: FC = () => {
  const { isConnected, updateWalletInfo } = useWallet();

  const connectWallet = async () => {
    const kit = new StellarWalletsKit({
      network: WalletNetwork.TESTNET,
      selectedWalletId: XBULL_ID,
      modules: allowAllModules(),
    });

    try {
      await kit.openModal({
        onWalletSelected: (option) => {
          // This must be synchronous
          kit.setWallet(option.id);

          // Handle async operations outside of the callback
          void (async () => {
            try {
              const { address } = await kit.getAddress();

              const server = new Horizon.Server("https://horizon-testnet.stellar.org", { allowHttp: true });

              const account: AccountResponse = await server.loadAccount(address);
              const balances: BalanceLine[] = account.balances;
              let xlmBalance: BalanceLineNative | undefined;

              const otherBalances: (
                | BalanceLineAsset<"credit_alphanum4" | "credit_alphanum12">
                | BalanceLineLiquidityPool
              )[] = [];
              balances.forEach((balance) => {
                if (balance.asset_type === "native") {
                  xlmBalance = balance;
                } else {
                  otherBalances.push(balance);
                }
              });

              const newBalance = xlmBalance ? xlmBalance.balance : "0";

              const signer = async (txXDR: string, networkPassphrase: string): Promise<string> => {
                const kitAny = kit as unknown as {
                  signTransaction: (...args: unknown[]) => Promise<string | { signedXDR?: string; signedTxXdr?: string }>;
                };
                const network =
                  String(networkPassphrase) === String(Networks.PUBLIC) ? WalletNetwork.PUBLIC : WalletNetwork.TESTNET;
                try {
                  const result = await kitAny.signTransaction(txXDR, { network });

                  // Handle string response
                  if (typeof result === "string") return result;

                  // Handle object response with different property names
                  if (typeof result === "object" && result !== null) {  
                    // Check for signedTxXdr (camelCase - what Freighter uses)
                    if ("signedTxXdr" in result) {
                      const maybe = (result as { signedTxXdr?: string }).signedTxXdr;
                      if (maybe) return maybe;
                    }
                    // Check for signedXDR (alternative format)
                    if ("signedXDR" in result) {
                      const maybe = (result as { signedXDR?: string }).signedXDR;
                      if (maybe) return maybe;
                    }
                  }
                } catch (err) {
                  // Fallback: try without options object
                  const fallback = await kitAny.signTransaction(txXDR, network);
                  if (typeof fallback === "string") return fallback;
                  if (typeof fallback === "object" && fallback !== null) {  
                    if ("signedTxXdr" in fallback) {
                      const maybe = (fallback as { signedTxXdr?: string }).signedTxXdr;
                      if (maybe) return maybe;
                    }
                    if ("signedXDR" in fallback) {
                      const maybe = (fallback as { signedXDR?: string }).signedXDR;
                      if (maybe) return maybe;
                    }
                  }
                  throw err instanceof Error ? err : new Error(String(err));
                }
                throw new Error("Wallet did not return a signed transaction");
              };

              updateWalletInfo(true, address, newBalance, otherBalances, signer);
            } catch (error) {
              console.error("Error loading account:", error);
              updateWalletInfo(true, null, "0", []);
            }
          })();
        },
      });
    } catch (error) {
      console.error("Error connecting wallet:", error);
    }
  };

  const disconnectWallet = async () => {
    updateWalletInfo(false, null, null, null);
  };

  return (
    <button
      onClick={() => {
        if (isConnected) {
          void disconnectWallet(); // Explicitly ignore the promise
        } else {
          void connectWallet(); // Explicitly ignore the promise
        }
      }}
      className={`w-full px-2 py-1.5 text-sm rounded-md ${isConnected
        ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
        : "bg-primary text-primary-foreground hover:bg-primary/90"
        }`}
    >
      {isConnected ? "Disconnect Wallet" : "Connect Wallet"}
    </button>
  );
};
